<mat-toolbar [class.with-shadow]="showShadow" class="topbar">
  <!-- Left Section - Logo -->
  <div class="navbar-left">
    <div class="logo-container" (click)="onLogoClick()">
      <!-- DreamBooks Pro Logo -->
      <img *ngIf="lastHome === AppState.HOME_DREAMBOOKSPRO"
           src="assets/logos/logo_letras_black.svg"
           alt="DreamBooksPro"
           class="logo-img" />

      <!-- OneLab Pro Logo Text -->
      <div class="logo-text" *ngIf="lastHome === AppState.HOME_ONELABPRO">
        <span class="logo-title">ONELABPRO.</span>
        <span class="logo-sub">PROFESSIONAL PRINTS</span>
      </div>

      <!-- LFM Pro Logo Text -->
      <div class="logo-text" *ngIf="lastHome === AppState.HOME_LFMPRO">
        <span class="logo-title">LFMPRO.</span>
        <span class="logo-sub">PROFESSIONAL PRINTS</span>
      </div>
    </div>

    <!-- Partner Logo -->
    <div *ngIf="partner()?.showLogo === 1" class="partner-logo">
      <div class="divider"></div>
      <img *ngIf="partner()?.logo" [src]="globals.getImageUrl(partner()!.logo)" [alt]="partner()?.name">
      <span *ngIf="!partner()?.logo" class="partner-name">{{ partner()?.name }}</span>
    </div>

    <!-- Staff/Support icon cluster (headset + bug list/report) -->
    @if (isLoggedIn()) {
      <div class="icon-cluster">
        <!-- Support Button (headset) — staff flag comes from the PARENT's
             typeId; never for sub-user sessions -->
        <button *ngIf="(isSupport$ | async) && perms.canAccount()" mat-icon-button (click)="onSupportClick()" class="nav-icon-btn support-btn"
                matTooltip="{{ 'TOPBAR.CUSTOMER_SUPPORT' | translate }}"
                [attr.aria-label]="'TOPBAR.CUSTOMER_SUPPORT' | translate">
          <app-support-icon class="support-icon" [size]="24"></app-support-icon>
        </button>

        <!-- Bug button: report a problem (available to everyone). The
             admin-only "view list" action lives in the user dropdown. -->
        <button mat-icon-button (click)="openBugReport()" class="nav-icon-btn support-btn"
                matTooltip="{{ 'TOPBAR.REPORT_BUG' | translate }}"
                [attr.aria-label]="'TOPBAR.REPORT_BUG' | translate">
          <mat-icon>bug_report</mat-icon>
        </button>

        <!-- "Novidades" (What's New): orange pill; the dot marks unseen
             entries and clears when the dialog is opened. -->
        <button type="button" class="whats-new-btn" (click)="openWhatsNew()"
                matTooltip="{{ 'TOPBAR.WHATS_NEW_TOOLTIP' | translate }}"
                [attr.aria-label]="'TOPBAR.WHATS_NEW' | translate">
          <mat-icon>campaign</mat-icon>
          @if (hasUnseenWhatsNew()) {
            <span class="whats-new-dot" aria-hidden="true"></span>
          }
        </button>
      </div>
    }

    <!-- Upload Progress (global — persists across route navigation) -->
    @if (uploadResilience.isUploading()) {
      <div class="upload-status">
        <mat-icon class="cloud-icon">cloud_upload</mat-icon>
        <div class="upload-progress-container">
          <div class="upload-count">{{ uploadResilience.completedCount() }}/{{ uploadResilience.totalCount() }}</div>
          <mat-progress-bar mode="determinate" [value]="uploadResilience.uploadProgress()"></mat-progress-bar>
          <div class="upload-file-name" [title]="uploadResilience.currentFileName()">{{ uploadResilience.currentFileName() }}</div>
        </div>
      </div>
    } @else if (uploadResilience.isReuploadProcessing()) {
      <div class="upload-status">
        <mat-icon class="cloud-icon">cloud_sync</mat-icon>
        <div class="upload-progress-container">
          <div class="upload-count">{{ uploadResilience.reuploadResolvedCount() }}/{{ uploadResilience.reuploadTotalCount() }}</div>
          <mat-progress-bar mode="determinate" [value]="uploadResilience.reuploadProgress()"></mat-progress-bar>
          <div class="upload-file-name" [title]="uploadResilience.reuploadCurrentFile()">{{ uploadResilience.reuploadCurrentFile() }}</div>
        </div>
      </div>
    } @else if (isUploading()) {
      <div class="upload-status" (click)="onUploadsClick()">
        <mat-icon class="cloud-icon">cloud_upload</mat-icon>
        <div class="upload-progress-container">
          <mat-progress-bar mode="determinate" [value]="uploadProgress()"></mat-progress-bar>
          <span class="upload-text">{{ 'TOPBAR.UPLOADING' | translate }}</span>
        </div>
      </div>
    }
  </div>

  <!-- Center Section - Navigation (each link also gated per sub-user module) -->
  <div class="navbar-menu" *ngIf="isLoggedIn()">
    <!-- Album Design: gated on the hasAlbumDesign feature flag -->
    <a *ngIf="hasAlbumDesign && perms.canAlbumDesign()" class="nav-link" (click)="onAlbumDesignClick()">
      <mat-icon class="nav-link-icon">auto_stories</mat-icon>
      <span class="nav-link-text">{{ 'TOPBAR.ALBUM_DESIGN' | translate }}</span>
    </a>
    <a *ngIf="perms.canProducts()" class="nav-link" (click)="onProductsClick()">
      <mat-icon class="nav-link-icon">inventory_2</mat-icon>
      <span class="nav-link-text">{{ 'TOPBAR.PRODUCTS' | translate }}</span>
    </a>
    <a *ngIf="perms.canProjects()" class="nav-link" (click)="onDraftsClick()"
       [matTooltip]="'TOPBAR.DRAFTS' | translate">
      <mat-icon class="nav-link-icon">view_kanban</mat-icon>
      <span class="nav-link-text">{{ 'TOPBAR.DRAFTS' | translate }}</span>
    </a>
    <a *ngIf="perms.canOrders()" class="nav-link" (click)="onOrdersClick()">
      <mat-icon class="nav-link-icon">receipt_long</mat-icon>
      <span class="nav-link-text">{{ 'TOPBAR.ORDERS' | translate }}</span>
    </a>
    <a *ngIf="perms.canCart()" class="nav-link" (click)="onCartClick()">
      <mat-icon class="nav-link-icon">shopping_cart</mat-icon>
      <span class="nav-link-text">{{ 'TOPBAR.CART' | translate }}</span>
      <span *ngIf="cartItemsCount() > 0" class="badge">{{ cartItemsCount() }}</span>
    </a>
    <a *ngIf="user()?.allowGalleries && perms.canGalleries()" class="nav-link" (click)="onGalleriesClick()">
      <mat-icon class="nav-link-icon">photo_library</mat-icon>
      <span class="nav-link-text">{{ 'TOPBAR.GALLERIES' | translate }}</span>
    </a>
  </div>

  <!-- Right Section - User Actions -->
  <div class="navbar-right">
    <ng-container *ngIf="isLoggedIn()">
      <!-- Collections Button (main-account surface — hidden for sub-users) -->
      <button *ngIf="user()?.hasCollectionRequest && perms.canAccount()" mat-icon-button (click)="onCollectionsClick()" class="nav-icon-btn"
              matTooltip="{{ 'TOPBAR.COLLECTION' | translate }}">
        <mat-icon>collections</mat-icon>
      </button>

      <!-- User Profile Dropdown -->
      <div class="user-menu" [matMenuTriggerFor]="userMenu">
        <!-- User Avatar. The wrapper carries the unread-replies badge: this
             menu is where "Bugs reportados" lives, so the badge sits on the
             control that actually leads to the messages — the bug icon only
             files a NEW report and could not open the thread it pointed at.
             (The badge can't live inside .user-avatar: that clips its
             children to keep the photo inside the rounded square.) -->
        <div class="avatar-wrap"
             [matTooltip]="bugUnreadCount() > 0 ? ('TOPBAR.MY_BUG_REPORTS_UNREAD' | translate:{ count: bugUnreadCount() }) : ''">
          <div class="user-avatar">
            <!-- The avatar is an absolute S3 URL built from the stored serverName;
                 if that object is missing the <img> would sit there as a broken
                 icon forever, so fall back to the initials on load failure. -->
            <img *ngIf="showAvatarImage()" [src]="avatarUrl()" class="avatar-img"
                 [alt]="fullName()" (error)="onAvatarError()">
            <span *ngIf="!showAvatarImage()" class="avatar-initials">{{ initials() }}</span>
          </div>
          @if (bugUnreadCount(); as unread) {
            <span class="avatar-badge" aria-hidden="true">{{ unread > 99 ? '99+' : unread }}</span>
          }
        </div>

        <!-- User Info -->
        <div class="user-info">
          <span class="user-name">{{ fullName() }}</span>
        </div>

        <!-- Active price-inflation chip — always visible while the factor is
             on, so inflated prices are never mistaken for real ones. -->
        <span *ngIf="priceInflation() > 1" class="inflation-chip">{{ inflationLabel() }}</span>

        <mat-icon class="dropdown-arrow">expand_more</mat-icon>
      </div>

      <!-- User Dropdown Menu -->
      <mat-menu #userMenu="matMenu" class="user-dropdown-menu">
        <button *ngIf="perms.canAccount()" mat-menu-item type="button" (click)="onProfileClick()">
          <mat-icon>person</mat-icon>
          <span>{{ 'TOPBAR.MY_PROFILE' | translate }}</span>
        </button>
        <!-- Workspace shortcut — same target and gate as the centre nav link.
             view_kanban: the Workspace IS a kanban board, and folder_open
             read too close to the "Projetos" folder icon below. -->
        <button *ngIf="perms.canProjects()" mat-menu-item type="button" (click)="onDraftsClick()">
          <mat-icon>view_kanban</mat-icon>
          <span>{{ 'TOPBAR.DRAFTS' | translate }}</span>
        </button>
        <button *ngIf="perms.isSubUser()" mat-menu-item type="button" (click)="openChangePassword()">
          <mat-icon>key</mat-icon>
          <span>{{ 'CUSTOMER_USERS.CHANGE_PASSWORD.TITLE' | translate }}</span>
        </button>
        <button *ngIf="perms.isSubUser() && perms.canInvoices()" mat-menu-item type="button" (click)="onInvoicesClick()">
          <mat-icon>request_quote</mat-icon>
          <span>{{ 'TOPBAR.INVOICES' | translate }}</span>
        </button>
        <!-- Staff tools follow the HUMAN at the keyboard: isDesigner/isAdmin/
             isSupport derive from the PARENT customer's typeId, so in a
             sub-user session they must not leak through (canAccount = not a
             sub-user). -->
        <button *ngIf="isDesigner && perms.canAccount()" mat-menu-item type="button" (click)="onDesignerClick()">
          <mat-icon>palette</mat-icon>
          <span>{{ 'TOPBAR.DESIGNER' | translate }}</span>
        </button>
        <!-- Projetos is ADMIN-only again (typeId 4 — the isAdmin gate that
             development had dropped, restored 2026-08-27); the PROJECTS
             module check stays for sub-user parity with the /projects
             route guard. -->
        <button *ngIf="isAdmin && perms.canProjects()" mat-menu-item type="button" (click)="onProjectsClick()">
          <mat-icon>folder</mat-icon>
          <span>{{ 'TOPBAR.PROJECTS' | translate }}</span>
        </button>
        <!-- The user's own reports + support thread. Available to everyone
             (the backend scopes it to the account); sub-users see the
             account's reports, matching how they file them. -->
        <button mat-menu-item type="button" (click)="openMyBugReports()">
          <mat-icon>forum</mat-icon>
          <span>{{ 'TOPBAR.MY_BUG_REPORTS' | translate }}</span>
          @if (bugUnreadCount(); as unread) {
            <span class="menu-badge">{{ unread > 99 ? '99+' : unread }}</span>
          }
        </button>
        <button *ngIf="isSupport && perms.canAccount()" mat-menu-item type="button" (click)="openBugList()">
          <mat-icon>bug_report</mat-icon>
          <span>{{ 'TOPBAR.VIEW_BUG_REPORTS' | translate }}</span>
        </button>
        <!-- Order grouping belongs to the ORDERS module (guide §4.1) — available
             to sub-users whose role includes it -->
        <button *ngIf="allowOrdersGrouping && perms.canOrders()" mat-menu-item type="button" (click)="openOrderGrouping()">
          <mat-icon>local_shipping</mat-icon>
          <span>{{ 'TOPBAR.GROUP_SHIPMENTS' | translate }}</span>
        </button>
        <button *ngIf="perms.canAccount()" mat-menu-item type="button" (click)="onToggleHidePrices()">
          <mat-icon>{{ appState.hidePrices() ? 'visibility' : 'visibility_off' }}</mat-icon>
          <span>{{ (appState.hidePrices() ? 'TOPBAR.SHOW_PRICES' : 'TOPBAR.HIDE_PRICES') | translate }}</span>
        </button>
        <!-- Display-only price inflation. A plain div (NOT mat-menu-item) so
             interacting with the slider doesn't close the menu. -->
        <div *ngIf="perms.canAccount()" class="inflation-menu-block" (click)="$event.stopPropagation()">
          <div class="inflation-label">
            <mat-icon>trending_up</mat-icon>
            <span>{{ 'TOPBAR.INFLATE_PRICES' | translate }}</span>
            <span class="inflation-current" [class.active]="priceInflation() > 1">{{ inflationLabel() }}</span>
          </div>
          <div class="inflation-slider-row">
            <input type="range" class="inflation-slider"
                   [min]="inflationMin" [max]="inflationMax" step="0.1"
                   [value]="priceInflation()"
                   (input)="onInflationInput($event)">
            <button type="button" class="inflation-reset"
                    [disabled]="priceInflation() <= 1"
                    (click)="resetInflation()">1.0x</button>
          </div>
        </div>
        <button mat-menu-item type="button" (click)="onLogoutClick()">
          <mat-icon>logout</mat-icon>
          <span>{{ 'TOPBAR.LOGOUT' | translate }}</span>
        </button>
      </mat-menu>
    </ng-container>

    <!-- Login Button for non-authenticated users -->
    <button *ngIf="!isLoggedIn()" mat-flat-button color="primary" (click)="onProfileClick()" class="login-btn">
      {{ 'TOPBAR.LOGIN' | translate }}
    </button>
  </div>
</mat-toolbar>
